import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import axios from "axios";
import liff from "@line/liff";
import Header from "../components/common/Header";
import Footer from "../components/common/Footer";
import { ensureLiffInit } from "../liffClient";

type Mistake = {
  word: string;
  image_url: string;
  meaning?: string;
  lang?: string;
  attempted_at: string;
};

// SpeechSynthesis に渡す BCP-47 の言語コード
const SPEECH_LANG: Record<string, string> = {
  english: "en-US",
  french: "fr-FR",
  japanese: "ja-JP",
};

const speak = (word: string, lang?: string) => {
  const instance = new SpeechSynthesisUtterance(word);
  instance.lang = SPEECH_LANG[lang ?? ""] ?? "en-US";
  window.speechSynthesis.speak(instance);
};

export default function QuizMistakes() {
  const [loggedIn, setLoggedIn] = useState(false);
  const [loginFailed, setLoginFailed] = useState(false);
  const [mistakes, setMistakes] = useState<Mistake[] | null>(null);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      const ready = await ensureLiffInit();
      const isLoggedIn = ready && liff.isLoggedIn();
      setLoggedIn(isLoggedIn);
      if (!isLoggedIn) return;

      try {
        const profile = await liff.getProfile();
        setUserId(profile.userId);
        const response = await axios.post("/v1/api/quiz/mistakes/fetch", {
          userId: profile.userId,
        });
        setMistakes([...(response.data.mistakes ?? [])].reverse());
      } catch (error) {
        console.error("Error fetching quiz mistakes:", error);
      }
    };

    load();
  }, []);

  const handleDelete = async (attemptedAt: string) => {
    if (!userId) return;
    try {
      await axios.post("/v1/api/quiz/mistake/delete", {
        userId,
        attemptedAt,
      });
      setMistakes((prev) => (prev ?? []).filter((m) => m.attempted_at !== attemptedAt));
    } catch (error) {
      console.error("Error deleting quiz mistake:", error);
    }
  };

  const handleLogin = async () => {
    const ok = await ensureLiffInit();
    if (!ok) {
      setLoginFailed(true);
      return;
    }
    liff.login();
  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f1f5f9", display: "flex", flexDirection: "column" }}>
      <Box sx={{ width: "100%", position: "sticky", top: "0", zIndex: 100 }}>
        <Header />
      </Box>

      <Box sx={{ maxWidth: 560, mx: "auto", px: 2, pt: 4, pb: 2, flex: 1, width: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 4 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              background: "linear-gradient(135deg, #4338ca, #6366f1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(99,102,241,0.4)",
            }}
          >
            <HistoryEduIcon sx={{ color: "white", fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "1.4rem", color: "#1e293b", lineHeight: 1.2 }}>
              間違えた問題
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", color: "#94a3b8" }}>
              クイズで間違えた単語の一覧
            </Typography>
          </Box>
        </Box>

        {!loggedIn && (
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: "16px",
              p: 3,
              textAlign: "center",
              boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
              border: "1px solid rgba(226,232,240,0.8)",
            }}
          >
            <Typography sx={{ color: "#64748b", fontSize: "0.9rem", mb: 2 }}>
              ログインすると間違えた問題を確認できます
            </Typography>
            <Button
              onClick={handleLogin}
              variant="contained"
              sx={{
                background: "linear-gradient(135deg, #4338ca, #6366f1)",
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                boxShadow: "0 4px 14px rgba(99,102,241,0.35)",
                "&:hover": { background: "linear-gradient(135deg, #3730a3, #4f46e5)" },
              }}
            >
              ログイン
            </Button>
            {loginFailed && (
              <Typography sx={{ color: "#ef4444", fontSize: "0.78rem", mt: 1.5 }}>
                ログインに接続できませんでした。時間をおいて再度お試しください。
              </Typography>
            )}
          </Box>
        )}

        {loggedIn && mistakes && mistakes.length === 0 && (
          <Typography sx={{ color: "#94a3b8", textAlign: "center", mt: 4 }}>
            間違えた問題はまだありません
          </Typography>
        )}

        {loggedIn &&
          mistakes?.map((item, i) => (
            <Box
              key={i}
              sx={{
                backgroundColor: "white",
                borderRadius: "16px",
                mb: 2.5,
                boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
                border: "1px solid rgba(226,232,240,0.8)",
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                p: 2,
                gap: 1.5,
              }}
            >
              {item.image_url && (
                <Box
                  component="img"
                  src={item.image_url}
                  alt={item.word}
                  sx={{
                    width: 72,
                    height: 72,
                    objectFit: "cover",
                    borderRadius: "12px",
                    flexShrink: 0,
                    border: "2px solid #f1f5f9",
                  }}
                />
              )}
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: "#1e293b", wordBreak: "break-word" }}>
                  {item.word}
                </Typography>
                {item.meaning && (
                  <Typography sx={{ fontSize: "0.88rem", color: "#64748b", fontWeight: 500, mt: 0.25, wordBreak: "break-word" }}>
                    {item.meaning}
                  </Typography>
                )}
                <Typography sx={{ fontSize: "0.78rem", color: "#94a3b8", mt: 0.5 }}>
                  {new Date(item.attempted_at).toLocaleString("ja-JP")}
                </Typography>
              </Box>

              <IconButton
                onClick={() => speak(item.word, item.lang)}
                sx={{
                  width: 40,
                  height: 40,
                  background: "linear-gradient(135deg, #4338ca, #6366f1)",
                  color: "white",
                  flexShrink: 0,
                  boxShadow: "0 3px 10px rgba(99,102,241,0.35)",
                  "&:hover": { background: "linear-gradient(135deg, #3730a3, #4f46e5)" },
                }}
              >
                <VolumeUpIcon sx={{ fontSize: 20 }} />
              </IconButton>

              <IconButton
                onClick={() => handleDelete(item.attempted_at)}
                sx={{
                  width: 36,
                  height: 36,
                  color: "#cbd5e1",
                  flexShrink: 0,
                  "&:hover": { color: "#ef4444", backgroundColor: "#fef2f2" },
                  transition: "color 0.15s, background-color 0.15s",
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>
          ))}
      </Box>

      <Footer />
    </Box>
  );
}
